import React from 'react';

const GLYPHS = {
  rp_state_squeeze: Squeeze,
  rp_state_pull:    Pull,
  rp_state_tilt:    Tilt,
  rp_state_grip:    Grip,
  rp_state_lock:    Lock,
  rp_state_twist:   Twist,
  rp_state_draw:    Draw,
  rp_state_lift:    Lift,
  rp_state_pulse:   Pulse,
  rp_state_release: Relax,
  rp_state_push:    Push,
  __fallback:       Fallback,
};

/**
 * SkillGlyph — an abstract, anatomically-inspired SVG glyph for each
 * named skill in the Year 2 vocabulary. The design is a small circle
 * (representing the pelvic floor zone) with motion marks that show
 * what the skill does: inward pulls for PULL, a clockwise spiral for
 * TWIST, a locked brace for LOCK, and so on.
 *
 * Pure presentation — no state, no animation (beyond optional
 * hover / aria-current accents via CSS on the consumer). The
 * glyphs share a 40×40 viewBox so the consumer can drop any skill
 * key into the same slot.
 *
 * Usage:
 *
 *   <SkillGlyph skill="rp_state_lock" color="var(--primary-dark)" />
 *
 * Falls back to a neutral dot if the skill key isn't recognised so
 * a future locale addition doesn't crash the UI.
 */
export default function SkillGlyph({ skill, color = 'currentColor', size = 40, label }) {
  const Glyph = GLYPHS[skill] || GLYPHS.__fallback;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={label || undefined}
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <Glyph />
    </svg>
  );
}

// ── Nine glyphs. Common anchor: a 40×40 viewBox with the zone circle
//    centered at (20, 22). Motion marks read as abstract intent, not
//    literal anatomy — they evoke the action without being clinical. ──

// SQUEEZE — concentric rings pulling inward
function Squeeze() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <circle cx="20" cy="22" r="5.5" strokeDasharray="2 2" />
      <circle cx="20" cy="22" r="2" fill="currentColor" />
    </>
  );
}

// PULL — inward arrows from both sides
function Pull() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 7 22 L 13 22 M 11 20 L 13 22 L 11 24" />
      <path d="M 33 22 L 27 22 M 29 20 L 27 22 L 29 24" />
    </>
  );
}

// TILT — canted line through the zone
function Tilt() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 12 28 L 28 16" />
      <circle cx="12" cy="28" r="1.8" fill="currentColor" />
      <circle cx="28" cy="16" r="1.8" fill="currentColor" />
    </>
  );
}

// GRIP — tight double ring with brace marks
function Grip() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <circle cx="20" cy="22" r="6" />
      <path d="M 14 14 L 11 11 M 26 14 L 29 11 M 14 30 L 11 33 M 26 30 L 29 33" />
    </>
  );
}

// LOCK — padlock glyph over the zone
function Lock() {
  return (
    <>
      <circle cx="20" cy="22" r="10" opacity="0.55" />
      <rect x="14" y="19" width="12" height="10" rx="1.5" fill="currentColor" fillOpacity="0.1" />
      <path d="M 16.5 19 V 16 A 3.5 3.5 0 0 1 23.5 16 V 19" />
      <circle cx="20" cy="24" r="1.2" fill="currentColor" />
      <path d="M 20 24 V 27" />
    </>
  );
}

// TWIST — clockwise spiral
function Twist() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 20 14 A 8 8 0 0 1 26 25 A 5 5 0 0 1 17 23 A 3 3 0 0 1 22 21" />
      <path d="M 24 17 L 26 14 L 23 13" />
    </>
  );
}

// DRAW — inward spiral + arrow
function Draw() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 12 22 Q 20 12 28 22 Q 22 30 17 24" />
      <path d="M 19 22 L 17 24 L 15 22" />
    </>
  );
}

// LIFT — upward arrow with rising ring
function Lift() {
  return (
    <>
      <circle cx="20" cy="22" r="10" opacity="0.5" />
      <path d="M 20 30 L 20 14" strokeWidth="1.8" />
      <path d="M 14 20 L 20 14 L 26 20" strokeWidth="1.8" />
      <circle cx="20" cy="14" r="1.5" fill="currentColor" />
    </>
  );
}

// PULSE — zigzag rhythm across the zone
function Pulse() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 11 22 L 14 22 L 16 18 L 19 26 L 22 18 L 25 22 L 29 22" strokeWidth="1.8" />
    </>
  );
}

// RELAX / RELEASE — softened ripple, no contraction
function Relax() {
  return (
    <>
      <circle cx="20" cy="22" r="10" strokeDasharray="3 3" />
      <path d="M 11 22 Q 15 19 20 22 T 29 22" />
      <path d="M 11 26 Q 15 23 20 26 T 29 26" opacity="0.55" />
    </>
  );
}

// PUSH — outward arrow
function Push() {
  return (
    <>
      <circle cx="20" cy="22" r="10" />
      <path d="M 20 28 L 20 14" strokeWidth="1.8" />
      <path d="M 14 20 L 20 14 L 26 20" strokeWidth="1.8" opacity="0.4" />
      <path d="M 14 26 L 20 32 L 26 26" strokeWidth="1.8" opacity="0.8" />
    </>
  );
}

// Fallback — a quiet dot so an unknown key renders cleanly
function Fallback() {
  return (
    <>
      <circle cx="20" cy="22" r="10" opacity="0.4" />
      <circle cx="20" cy="22" r="2" fill="currentColor" opacity="0.5" />
    </>
  );
}

